import { useAuth } from "@clerk/react";
import { ManagedRelay, relayPrincipalKey } from "@maito/client-runtime/relay";
import { reportAtomCommandResult, settleAsyncResult } from "@maito/client-runtime/state/runtime";
import * as Effect from "effect/Effect";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { environmentCatalog } from "../connection/catalog";
import { runtime } from "../lib/runtime";
import { useAtomCommand } from "../state/use-atom-command";
import { resolveRelayClerkTokenOptions } from "./publicConfig";
import { confirmStartupAccount, STARTUP_ACCOUNT_STORAGE_KEY } from "../startup/account";
import { clearOperatorCache } from "../startup/operatorCache";
import { markStartup } from "../startup/timing";
import {
  activateManagedRelayAuthentication,
  deactivateManagedRelayAuthentication,
} from "./managedRelaySession";

export type ManagedRelayAuthState =
  | { readonly status: "loading" }
  | { readonly status: "signed-out" }
  | { readonly status: "organization-required" }
  | { readonly status: "activating" }
  | {
      readonly status: "signed-in";
      readonly accountId: string;
      readonly organizationId: string;
    };

const ManagedRelayAuthContext = createContext<ManagedRelayAuthState | null>(null);

export function useManagedRelayAuth(): ManagedRelayAuthState {
  const auth = useContext(ManagedRelayAuthContext);
  if (auth === null) {
    throw new Error("Maito account authentication is not configured.");
  }
  return auth;
}

export function ManagedRelayAuthProvider({ children }: { readonly children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn, orgId, userId } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const removeRelayEnvironments = useAtomCommand(environmentCatalog.removeRelayEnvironments, {
    reportFailure: false,
    reportDefect: false,
  });
  const observedAccountRef = useRef<string | null | undefined>(undefined);
  const accountTransitionRef = useRef<Promise<void> | null>(null);
  const [activeIdentity, setActiveIdentity] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (!isLoaded) {
      return;
    }

    let cancelled = false;
    const previousAccount = observedAccountRef.current;
    const nextAccount = isSignedIn && userId && orgId ? relayPrincipalKey(userId, orgId) : null;
    confirmStartupAccount(userId && orgId ? { accountId: userId, organizationId: orgId } : null);
    markStartup("auth");
    observedAccountRef.current = nextAccount;

    const queueAccountCleanup = () => {
      const previousTransition = accountTransitionRef.current ?? Promise.resolve();
      const transition = previousTransition.then(async () => {
        await clearOperatorCache().catch(() => undefined);
        const results = await Promise.all([
          removeRelayEnvironments(),
          settleAsyncResult(() =>
            runtime.runPromiseExit(
              ManagedRelay.ManagedRelayClient.pipe(
                Effect.flatMap((client) => client.resetTokenCache),
              ),
            ),
          ),
        ]);
        for (const result of results) {
          reportAtomCommandResult(result, { label: "cloud account cleanup" });
        }
      });
      accountTransitionRef.current = transition;
      void transition.finally(() => {
        if (accountTransitionRef.current === transition) {
          accountTransitionRef.current = null;
        }
      });
      return transition;
    };

    if (!isSignedIn || !userId) {
      setActiveIdentity(null);
      deactivateManagedRelayAuthentication();
      if (previousAccount !== undefined && previousAccount !== null) {
        void queueAccountCleanup();
      }
    } else if (orgId) {
      const tokenProvider = () => getToken(resolveRelayClerkTokenOptions());
      if (previousAccount === nextAccount && activeIdentity === nextAccount) {
        activateManagedRelayAuthentication(userId, orgId, tokenProvider);
        return;
      }
      const activateSession = () => {
        if (!cancelled) {
          activateManagedRelayAuthentication(userId, orgId, tokenProvider);
          setActiveIdentity(nextAccount);
        }
      };
      const activateAfterTransition = (transition: Promise<void> | null) => {
        if (transition === null) {
          activateSession();
          return;
        }
        void (async () => {
          await transition;
          activateSession();
        })();
      };
      if (
        previousAccount !== undefined &&
        previousAccount !== null &&
        previousAccount !== nextAccount
      ) {
        setActiveIdentity(null);
        deactivateManagedRelayAuthentication();
        activateAfterTransition(queueAccountCleanup());
      } else {
        activateAfterTransition(accountTransitionRef.current);
      }
    } else {
      setActiveIdentity(null);
      deactivateManagedRelayAuthentication();
      if (previousAccount !== undefined && previousAccount !== null) void queueAccountCleanup();
    }
    return () => {
      cancelled = true;
    };
  }, [activeIdentity, getToken, isLoaded, isSignedIn, orgId, removeRelayEnvironments, userId]);

  useEffect(() => () => deactivateManagedRelayAuthentication(), []);

  useEffect(() => {
    const accountChanged = (event: StorageEvent) => {
      if (event.key === STARTUP_ACCOUNT_STORAGE_KEY && event.oldValue !== event.newValue) {
        deactivateManagedRelayAuthentication();
        window.location.reload();
      }
    };
    window.addEventListener("storage", accountChanged);
    return () => window.removeEventListener("storage", accountChanged);
  }, []);

  const authState = useMemo<ManagedRelayAuthState>(() => {
    if (!isLoaded) return { status: "loading" };
    if (!isSignedIn || !userId) return { status: "signed-out" };
    if (!orgId) return { status: "organization-required" };
    if (activeIdentity !== relayPrincipalKey(userId, orgId)) return { status: "activating" };
    return {
      status: "signed-in",
      accountId: userId,
      organizationId: orgId,
    };
  }, [activeIdentity, isLoaded, isSignedIn, orgId, userId]);

  return (
    <ManagedRelayAuthContext.Provider value={authState}>
      {children}
    </ManagedRelayAuthContext.Provider>
  );
}
